import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import API from '../config/api';
import Swal from 'sweetalert2';

const Register = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    username: '',
    email: '',
    code: '',
    user_type: '',
    password: '',
    confirm_password: '',
    pin: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
    setFormData(prev => ({ ...prev, pin: value }));
  };

  const validateInput = (name, value) => {
    const newErrors = { ...errors };
    delete newErrors[name];

    if (!value || value.trim() === '') {
      newErrors[name] = true;
      setErrors(newErrors);
      return false;
    }

    switch (name) {
      case 'first_name':
      case 'last_name':
        if (!/^[a-zA-Z ]+$/.test(value.trim())) {
          newErrors[name] = true;
          setErrors(newErrors);
          return false;
        }
        break;

      case 'email':
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          newErrors[name] = true;
          setErrors(newErrors);
          return false;
        }
        break;

      case 'password':
        if (value.length < 6) {
          newErrors[name] = true;
          setErrors(newErrors);
          return false;
        }
        if (formData.confirm_password && value !== formData.confirm_password) {
          newErrors.confirm_password = true;
        } else {
          delete newErrors.confirm_password;
        }
        break;

      case 'confirm_password':
        if (value !== formData.password) {
          newErrors[name] = true;
          setErrors(newErrors);
          return false;
        }
        break;

      case 'pin':
        if (!/^[0-9]{4}$/.test(value.trim())) {
          newErrors[name] = true;
          setErrors(newErrors);
          return false;
        }
        break;

      case 'user_type':
        if (!['qurator', 'qure'].includes(value.trim())) {
          newErrors[name] = true;
          setErrors(newErrors);
          return false;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'pin') {
      handlePinChange(e);
    } else {
      validateInput(name, value);
    }
  };

  useEffect(() => {
    if (formData.confirm_password && formData.password !== formData.confirm_password) {
      setErrors(prev => ({ ...prev, confirm_password: true }));
    } else if (formData.confirm_password && formData.password === formData.confirm_password) {
      setErrors(prev => {
        const next = { ...prev };
        delete next.confirm_password;
        return next;
      });
    }
  }, [formData.password, formData.confirm_password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    let isValid = true;
    const newErrors = {};

    Object.keys(formData).forEach(key => {
      if (!formData[key] || formData[key].trim() === '') {
        newErrors[key] = true;
        isValid = false;
      } else if (!validateInput(key, formData[key])) {
        isValid = false;
      }
    });

    if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = true;
      isValid = false;
    }

    setErrors(newErrors);

    if (!isValid) {
      setLoading(false);
      Swal.fire({
        title: 'Validation Error',
        text: 'Please check all required fields',
        icon: 'error',
        confirmButtonColor: '#D95A17'
      });
      return;
    }

    try {
      const response = await API.post('/auth/register', formData);

      if (response.data.status === 'success') {
        Swal.fire({
          title: 'Success!',
          text: 'Your account has been created successfully',
          icon: 'success',
          confirmButtonColor: '#D95A17'
        }).then((result) => {
          if (result.isConfirmed) {
            navigate('/login');
          }
        });
      } else {
        Swal.fire({
          title: 'Registration Failed',
          text: response.data.message || 'Registration failed. Please try again.',
          icon: 'error',
          confirmButtonColor: '#D95A17'
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred. Please try again.';
      Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error',
        confirmButtonColor: '#D95A17'
      });
    } finally {
      setLoading(false);
    }
  };

  const controlClass = (invalid) =>
    [
      'h-[52px] max-sm:h-[52px] w-full rounded-xl border-2 bg-white pl-14 pr-4 text-base transition outline-none md:h-[54px]',
      invalid ? 'border-red-500 shadow-none' : 'border-slate-200 focus:border-brand focus:ring-4 focus:ring-brand-light',
      'disabled:cursor-not-allowed disabled:opacity-70'
    ].join(' ');

  const selectClass = (invalid) =>
    [
      'h-[52px] w-full appearance-auto rounded-xl border-2 bg-white py-0 pl-14 pr-4 text-base transition outline-none md:h-[54px]',
      invalid ? 'border-red-500' : 'border-slate-200 focus:border-brand focus:ring-4 focus:ring-brand-light',
      'disabled:cursor-not-allowed disabled:opacity-70'
    ].join(' ');

  return (
    <div className="relative z-10 flex min-h-screen items-center justify-center p-6 sm:p-8">
      <div className="flex w-full max-w-[1100px] overflow-hidden rounded-3xl bg-white shadow-[0_10px_30px_rgba(0,0,0,0.1)] max-lg:max-w-[600px]">
        <div
          className="hidden w-[450px] shrink-0 flex-col justify-center bg-gradient-to-br from-[rgba(44,62,80,0.97)] to-[rgba(217,90,23,0.9)] p-12 text-white lg:flex"
          style={{ clipPath: 'polygon(0 0, 95% 0, 100% 100%, 0 100%)' }}
        >
          <h2 className="mb-4 text-2xl font-semibold">Join Qure Today</h2>
          <p className="mb-4">Create your account and access powerful research tools and protocols.</p>
          <div className="mt-8 flex flex-col gap-4">
            <div className="flex items-center gap-3 text-base">
              <i className="bi bi-shield-check mr-2 text-2xl" />
              <span>Secure Platform</span>
            </div>
            <div className="flex items-center gap-3 text-base">
              <i className="bi bi-gear mr-2 text-2xl" />
              <span>Advanced Tools</span>
            </div>
            <div className="flex items-center gap-3 text-base">
              <i className="bi bi-people mr-2 text-2xl" />
              <span>Collaborative Research</span>
            </div>
            <div className="flex items-center gap-3 text-base">
              <i className="bi bi-graph-up mr-2 text-2xl" />
              <span>Data Analytics</span>
            </div>
          </div>
        </div>

        <div className="relative z-[2] min-w-0 flex-1 bg-white px-6 py-10 max-lg:ml-0 max-lg:rounded-3xl max-lg:px-10 max-sm:px-6 sm:px-12 lg:-ml-5 lg:rounded-l-3xl lg:rounded-r-none lg:px-12 lg:py-12">
          <img
            src="https://qure.science/images/qure.webp"
            alt="Qure"
            className="mb-8 h-auto w-[140px] max-lg:mx-auto max-sm:mb-6 max-sm:w-[120px] cursor-pointer"
            style={{
              filter:
                'invert(44%) sepia(91%) saturate(2044%) hue-rotate(347deg) brightness(99%) contrast(95%)'
            }}
            onClick={() => navigate('/login')}
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/140x60?text=Qure';
            }}
          />

          <h1 className="mb-2 text-[1.75rem] font-semibold text-qure-secondary max-lg:text-center max-sm:text-2xl">
            Create Account
          </h1>
          <p className="mb-8 text-gray-500 max-lg:text-center">Join our research community</p>

          <form id="registerForm" onSubmit={handleSubmit} noValidate>
            <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-6">
              <div className="relative mb-0 lg:mb-0">
                <input
                  type="text"
                  className={controlClass(errors.first_name)}
                  name="first_name"
                  placeholder="First Name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
                <i className="bi bi-person pointer-events-none absolute left-5 top-1/2 z-[1] -translate-y-1/2 text-xl text-gray-400" />
              </div>
              <div className="relative">
                <input
                  type="text"
                  className={controlClass(errors.last_name)}
                  name="last_name"
                  placeholder="Last Name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
                <i className="bi bi-person pointer-events-none absolute left-5 top-1/2 z-[1] -translate-y-1/2 text-xl text-gray-400" />
              </div>
            </div>

            <div className="relative mb-6">
              <input
                type="text"
                className={controlClass(errors.username)}
                name="username"
                placeholder="Username"
                value={formData.username}
                onChange={handleChange}
                required
                disabled={loading}
              />
              <i className="bi bi-at pointer-events-none absolute left-5 top-1/2 z-[1] -translate-y-1/2 text-xl text-gray-400" />
            </div>

            <div className="relative mb-6">
              <input
                type="email"
                className={controlClass(errors.email)}
                name="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
              />
              <i className="bi bi-envelope pointer-events-none absolute left-5 top-1/2 z-[1] -translate-y-1/2 text-xl text-gray-400" />
            </div>

            <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="relative">
                <input
                  type="text"
                  className={controlClass(errors.code)}
                  name="code"
                  placeholder="Organization Code"
                  value={formData.code}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
                <i className="bi bi-building pointer-events-none absolute left-5 top-1/2 z-[1] -translate-y-1/2 text-xl text-gray-400" />
              </div>
              <div className="relative">
                <select
                  className={selectClass(errors.user_type)}
                  name="user_type"
                  value={formData.user_type}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <option value="">Select User Type</option>
                  <option value="qurator">Qurator</option>
                  <option value="qure">Qure</option>
                </select>
                <i className="bi bi-person-badge pointer-events-none absolute left-5 top-1/2 z-[1] -translate-y-1/2 text-xl text-gray-400" />
              </div>
            </div>

            <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="relative">
                <input
                  type="password"
                  className={controlClass(errors.password)}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
                <i className="bi bi-lock pointer-events-none absolute left-5 top-1/2 z-[1] -translate-y-1/2 text-xl text-gray-400" />
              </div>
              <div className="relative">
                <input
                  type="password"
                  className={controlClass(errors.confirm_password)}
                  name="confirm_password"
                  placeholder="Confirm Password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
                <i className="bi bi-lock-fill pointer-events-none absolute left-5 top-1/2 z-[1] -translate-y-1/2 text-xl text-gray-400" />
              </div>
            </div>

            <div className="relative mb-6">
              <input
                type="text"
                className={controlClass(errors.pin)}
                name="pin"
                placeholder="4-Digit PIN"
                maxLength={4}
                value={formData.pin}
                onChange={handleChange}
                required
                disabled={loading}
              />
              <i className="bi bi-shield-lock pointer-events-none absolute left-5 top-1/2 z-[1] -translate-y-1/2 text-xl text-gray-400" />
            </div>

            <div className="grid gap-4">
              <button
                type="submit"
                className="full-width-btn flex h-[54px] max-sm:h-[52px] items-center justify-center gap-3 rounded-xl border-0 bg-brand text-base font-semibold text-white transition hover:-translate-y-0.5 hover:bg-brand-dark hover:shadow-[0_8px_20px_rgba(217,90,23,0.2)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"
                      role="status"
                      aria-hidden="true"
                    />
                    Creating Account...
                  </>
                ) : (
                  <>
                    <i className="bi bi-person-plus mr-2" />
                    Create Account
                  </>
                )}
              </button>
              <Link
                to="/login"
                className="full-width-btn flex h-[54px] max-sm:h-[52px] items-center justify-center gap-3 rounded-xl border-2 border-gray-500 bg-white text-center text-base font-semibold text-gray-700 no-underline transition hover:border-gray-700 hover:bg-slate-100 hover:text-gray-900"
              >
                Already have an account? Sign In
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
